import { z } from 'zod';

/**
 * The shop's own delivery schedules: "Express, 3 days", "Normal, 7 days".
 *
 * A schedule is a CALCULATOR, not a record. Picking one at the counter fills in the due date —
 * order date plus its days — and that due date is what the order stores and every screen reads
 * afterwards. The order keeps no pointer back to the schedule it was worked out from, which is
 * why renaming "Express" or deleting it later cannot strand a single booking.
 *
 * The consequence worth knowing: a shop that changes Express from 3 days to 5 changes nothing
 * about the orders already promised, and that is the right answer — those dates were told to a
 * customer.
 */

/** Enough for the handful of speeds a counter actually offers, and few enough to fit a dropdown. */
export const MAX_DELIVERY_SCHEDULES = 12;

/** A fortnight of Sundays either side of a year; longer than any shop promises in days. */
export const MAX_DELIVERY_SCHEDULE_DAYS = 365;

export const deliveryScheduleSchema = z.object({
  /**
   * What the counter sees in the dropdown. It is also the identity of the row — the list holds
   * no ids because nothing outside the list ever points at one.
   */
  name: z.string().trim().min(1, 'Give the schedule a name').max(40),
  /** Days from the order date to the due date. Zero means same-day. */
  days: z.number().int().min(0).max(MAX_DELIVERY_SCHEDULE_DAYS),
});
export type DeliverySchedule = z.infer<typeof deliveryScheduleSchema>;

/**
 * The whole list, replaced wholesale the way working hours are: a shop edits its schedules on
 * one screen and saves them together, and a partial write of a list this small buys nothing.
 *
 * Names must be distinct, and case-insensitively so — two rows called "Express" and "express"
 * are indistinguishable in the dropdown they exist to fill.
 */
export const deliverySchedulesSchema = z
  .array(deliveryScheduleSchema)
  .max(MAX_DELIVERY_SCHEDULES)
  .refine(
    (schedules) =>
      new Set(schedules.map((schedule) => schedule.name.toLowerCase())).size === schedules.length,
    { message: 'Two schedules cannot share a name' },
  );

/** `Express (3 days)` — how a schedule reads in the dropdown. */
export function deliveryScheduleLabel(schedule: DeliverySchedule): string {
  if (schedule.days === 0) return `${schedule.name} (same day)`;
  return `${schedule.name} (${schedule.days} ${schedule.days === 1 ? 'day' : 'days'})`;
}

/**
 * The due date a schedule works out from the order date, as `YYYY-MM-DD`.
 *
 * Calendar days, not working days: a shop that is shut on Sunday still promised Tuesday, and
 * the capacity warning — which does know about the workload of a given day — is where an
 * unachievable promise gets questioned. Doing it here as well would mean two answers to
 * "when is this due?" and a counter that cannot predict either.
 *
 * Returns `null` when the order date is not a date, so a half-typed field simply leaves the
 * due date alone rather than filling it with nonsense.
 */
export function dueDateFrom(orderDate: string, days: number): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(orderDate.trim());
  if (!match) return null;

  const [, year, month, day] = match;
  // UTC throughout: this is date arithmetic on a calendar date, and a local-time midnight in a
  // half-hour zone is how "+3 days" becomes "+2 days and 23 hours" in the wrong season.
  const start = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (Number.isNaN(start.getTime())) return null;

  start.setUTCDate(start.getUTCDate() + days);
  return start.toISOString().slice(0, 10);
}
